import { Component, Inject, OnInit, Renderer2 } from '@angular/core';
import { Observable, Subject, takeUntil } from 'rxjs';
import { DataService } from '../../../services/data/data.service';
import { DOCUMENT } from '@angular/common';
import { IContract, IContractData } from '../../../interfaces/IJob';
import { FlagCxOneEnabled } from '../../../utils/feature-flags';
import { CX_ONE_CONFIG } from '../../../utils/constants';
import { ICxOneWindow } from '../../../interfaces/IChatBot';

@Component({
  selector: 'app-cx-one-chatbot',
  template: '<ng-content></ng-content>',
})
export class CxOneChatbotComponent implements OnInit {
  private readonly cleaner$ = new Subject<void>();
  private isScriptLoaded = false;
  public contractData: IContractData | null = null;
  readonly isCxOneEnabled = FlagCxOneEnabled;
  user$: Observable<any> | undefined;

  constructor(
    private readonly dataService: DataService,
    private readonly renderer2: Renderer2,
    @Inject(DOCUMENT) private readonly doc: Document
  ) { }

  ngOnInit(): void {
    this.subscribeToContractData();
  }

  private subscribeToContractData(): void {
    this.user$ = this.dataService.user$;
    if (this.user$) {
      this.dataService.contractData$
        .pipe(takeUntil(this.cleaner$))
        .subscribe({
          next: (contract) => {
            if (contract) {
              this.contractData = contract;
              if (this.shouldLoadChatBot) {
                this.loadChatBotScript(this.contractData.baseContract);
              }
            }
          },
          error: (error) => console.error('Error loading contract data:', error)
        });
    }

  }

  private get shouldLoadChatBot(): boolean {
    return Boolean(this.isCxOneEnabled && this.contractData?.baseContract);
  }

  private loadChatBotScript(baseContract: IContract): void {
    if (this.isScriptLoaded) {
      this.setCXOneChatFields(baseContract);
      return;
    }

    try {
      this.isScriptLoaded = true;
      const initScript = this.renderer2.createElement('script');
      initScript.setAttribute('id', 'cxoneScript');
      initScript.type = 'text/javascript';
      initScript.text = `
      window.CXoneDfo = 'cxone';
      window.cxone = window.cxone || function() {
        (window.cxone.q = window.cxone.q || []).push(arguments);
      };
      window.cxone.u = '${CX_ONE_CONFIG.SCRIPT_URL}';
    `;
      this.renderer2.appendChild(this.doc.head, initScript);

      const loaderScript = this.renderer2.createElement('script');
      loaderScript.type = 'text/javascript';
      loaderScript.src = CX_ONE_CONFIG.SCRIPT_URL + '?' + Math.round(Date.now() / 1e3 / 3600);

      loaderScript.onload = async () => {
        const { firstName } = baseContract.primaryCustomer;
        const { contractNumber, clientId, propertyAddress } = baseContract;
        const { addressLine1, city, zip } = propertyAddress;
        const address = `${addressLine1}, ${city}, ${zip}`;

        await this.delay(500);
        (window as unknown as ICxOneWindow).cxone('init', CX_ONE_CONFIG.INIT_ID);
        await this.delay(500);
        (window as unknown as ICxOneWindow).cxone('guide', 'init', CX_ONE_CONFIG.GUIDE_INIT_ID);

        (window as unknown as ICxOneWindow).cxone('chat', 'setCustomerName', this.escapeJS(firstName));
        (window as unknown as ICxOneWindow).cxone('chat', 'setCaseCustomField', 'chat_reason', '');
        (window as unknown as ICxOneWindow).cxone('chat', 'setCaseCustomField', 'contract_number', this.escapeJS(contractNumber));
        (window as unknown as ICxOneWindow).cxone('chat', 'setCaseCustomField', 'client', this.escapeJS(clientId));
        (window as unknown as ICxOneWindow).cxone('chat', 'setCaseCustomField', 'address', this.escapeJS(address));
        (window as unknown as ICxOneWindow).cxone('guide', 'hidePreSurvey');
        (window as unknown as ICxOneWindow).cxone('chat', 'setCustomCss', CX_ONE_CONFIG.CUSTOM_CSS);
      };

      loaderScript.onerror = () => {
        console.error('Failed to load CXOne script');
        this.isScriptLoaded = false;
      };

      this.renderer2.appendChild(this.doc.head, loaderScript);
    } catch (error) {
      console.error('Error loading CX One script:', error);
      this.isScriptLoaded = false;
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private setCXOneChatFields(baseContract: IContract): void {
    const cxone = (window as unknown as ICxOneWindow).cxone;
    if (typeof cxone === 'function') {
      const { firstName } = baseContract.primaryCustomer;
      const { contractNumber, clientId, propertyAddress } = baseContract;
      const { addressLine1, city, zip } = propertyAddress;
      const address = `${addressLine1}, ${city}, ${zip}`;

      cxone('chat', 'setCustomerName', this.escapeJS(firstName));
      cxone('chat', 'setCaseCustomField', 'chat_reason', '');
      cxone('chat', 'setCaseCustomField', 'contract_number', this.escapeJS(contractNumber));
      cxone('chat', 'setCaseCustomField', 'client', this.escapeJS(clientId));
      cxone('chat', 'setCaseCustomField', 'address', this.escapeJS(address));
    }
  }

  private escapeJS(value: string): string {
    return value.replace(/\\/g, '\\\\').replace(/'/g, '\\\'').replace(/"/g, '\\"');
  }

  ngOnDestroy(): void {
    this.cleaner$.next();
    this.cleaner$.complete();
  }
}